import { AxiosInstance, AxiosPromise, AxiosRequestConfig } from 'axios';

import type {
  QueryErrorDetailResponse,
  QueryErrorQueriesResponse,
  QueryErrorsResponse,
} from '@cast/types';

/**
 * TEMPORARY (POLY-2202): hand-written upstream query-errors endpoints.
 *
 * The database-optimizer API does not expose this surface yet. When the
 * upstream-errors spec lands, delete this module and point the three
 * methods spread into `dbo-api-adapter.ts` at the generated client
 * instead. Wire types live in `@cast/types` (`api/dbo.ts`); endpoint
 * paths and response shapes are proposals shared with the backend ticket.
 */

export type GetQueryErrorsParams = {
  organizationId: string;
  /** Database instance (server) id */
  instanceId: string;
  startTime?: string;
  endTime?: string;
  /** Logical database name to filter by */
  database?: string;
};

export type GetQueryErrorDetailParams = {
  organizationId: string;
  /** Database instance (server) id */
  instanceId: string;
  /** SQLSTATE-style error code */
  errorId: string;
};

export type GetQueryErrorQueriesParams = GetQueryErrorDetailParams & {
  startTime?: string;
  endTime?: string;
};

const SEGMENT = '/database-optimizer/query-errors/v1alpha/organizations';

type QueryErrorsPathArgs = {
  organizationId: string;
  instanceId: string;
  errorId?: string;
  suffix?: string;
};

const buildPath = ({
  organizationId,
  instanceId,
  errorId,
  suffix,
}: QueryErrorsPathArgs) => {
  let path = `${SEGMENT}/${encodeURIComponent(
    String(organizationId)
  )}/database-instances/${encodeURIComponent(String(instanceId))}/query-errors`;
  if (errorId !== undefined) {
    path += `/${encodeURIComponent(String(errorId))}`;
  }
  if (suffix) {
    path += suffix;
  }
  return path;
};

const toQuery = (params: {
  startTime?: string;
  endTime?: string;
  database?: string;
}) => {
  const query: Record<string, string> = {};
  if (params.startTime) query.start_time = params.startTime;
  if (params.endTime) query.end_time = params.endTime;
  if (params.database) query.database = params.database;
  return query;
};

export const makeDboQueryErrorsApi = (client: AxiosInstance) => ({
  /**
   * List upstream query errors for a database instance: totals, per-issue-type
   * breakdown, timeseries and the per-error-code table rows.
   */
  getQueryErrors: (
    params: GetQueryErrorsParams,
    options: AxiosRequestConfig = {}
  ): AxiosPromise<QueryErrorsResponse> =>
    client.get(buildPath(params), {
      ...options,
      params: toQuery(params),
    }),

  /** Single upstream error: long description, raw message and timeseries. */
  getQueryErrorDetail: (
    params: GetQueryErrorDetailParams,
    options: AxiosRequestConfig = {}
  ): AxiosPromise<QueryErrorDetailResponse> =>
    client.get(buildPath(params), options),

  /** Queries (statement templates) that produced a given upstream error. */
  getQueryErrorQueries: (
    params: GetQueryErrorQueriesParams,
    options: AxiosRequestConfig = {}
  ): AxiosPromise<QueryErrorQueriesResponse> =>
    client.get(buildPath({ ...params, suffix: '/queries' }), {
      ...options,
      params: toQuery(params),
    }),
});
